import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import withSearchResultsManagement from '~/components/common/search/with-search-results-management';
import withAlgoliaSearch from '~/components/common/search/with-algolia-search';
import AcademyIndices from '../../../algolia/academy/academy-indices';
import Dropdown from '../../widget/dropdown';
import ColorUtils from '../../../common/color-utils';

/**
 Runs an Algolia search to find content based off of activeFilters.
 Passes results back to AcademyLearnerHome, which then renders the content.
 Note that the record from Algolia must contain all needed info to render content.
 */
class AcademyContentFiltersBase extends Component {
  state = {
    activeFilters: {},
    clearFilteringStateAfterUpdate: false,
  };

  constructor(props) {
    super(props);

    _.each(this.props.filters, (filter) => {
      _.each(filter.options, (_option) => {
        this.state.activeFilters[filter.type] = [];
      });
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.results !== prevProps.results) {
      if (this.state.clearFilteringStateAfterUpdate) {
        this.setState({ clearFilteringStateAfterUpdate: false });
        this.clearFilteringState();
      } else {
        this.props.updateFilteringState(false);
        this.props.updateFilteredState(true);
        this.props.renderResults(this.props.results);
      }
    }
  }

  render() {
    return (
      <AcademyContentFilterWrapper
        align={this.props.align}
        className="academy-content-filter-wrapper"
        results={this.props.results}
        style={this.props.style || {}}
      >
        {this.renderFilters()}
      </AcademyContentFilterWrapper>
    );
  }

  renderFilters = () => {
    const filters = [];
    const activeColor = this.props.activeColor || '#D4E2FC';
    const activeTextColor = ColorUtils.getTextColorForHex(activeColor);
    const dropdownAlignment =
      this.props.align === 'right'
        ? { top: '35px', right: '0', bottom: 'auto', left: 'auto' }
        : { top: '35px', right: 'auto', bottom: 'auto', left: '0' };
    _.each(this.props.filters, (filter) => {
      const dropdownButton = (
        <AcademyContentFilterButton
          active={!_.isEmpty(this.state.activeFilters[filter.type])}
          activeColor={activeColor}
          activeTextColor={activeTextColor}
          className="academy-content-filter-button"
        >
          {this.props.filterLabels[filter.type]}
          {!_.isEmpty(this.state.activeFilters[filter.type]) && ` (${this.state.activeFilters[filter.type].length})`}
        </AcademyContentFilterButton>
      );
      filters.push(
        <Dropdown
          button={dropdownButton}
          height={this.props.filterHeight}
          position={dropdownAlignment}
          allowDropdownClick
        >
          {_.map(filter.options, (option) => this.renderFilterItem(filter.type, option))}
        </Dropdown>,
      );
    });

    return filters;
  };

  renderFilterItem = (filterType, option) => (
    <li>
      <label>
        <input
          onChange={() => {
            this.props.updateFilteringState(true);
            let updatedFiltersForType = this.state.activeFilters[filterType];
            if (_.includes(updatedFiltersForType, option)) {
              updatedFiltersForType = _.without(updatedFiltersForType, option);
            } else {
              updatedFiltersForType.push(option);
            }
            this.setState(
              {
                activeFilters: {
                  ...this.state.activeFilters,
                  [filterType]: updatedFiltersForType,
                },
              },
              () => this.search(),
            );
          }}
          type="checkbox"
        />
        {option}
      </label>
    </li>
  );

  search = () => {
    const userFilters = [];
    _.each(this.state.activeFilters, (activeFilters, filterType) => {
      if (!_.isEmpty(activeFilters)) {
        userFilters.push(
          `(${_.map(activeFilters, (activeFilter) => `${filterType}:"${_.escape(activeFilter)}"`).join(' OR ')})`,
        );
      }
    });
    if (!_.isEmpty(userFilters)) {
      const searchId = this.props.beforeSearch();
      const filters = ['viewable:true'];
      if (this.props.validContentTypes && _.isEmpty(this.state.activeFilters.type)) {
        filters.push(
          `(${_.map(this.props.validContentTypes, (validContentType) => `type:"${_.escape(validContentType)}"`).join(
            ' OR ',
          )})`,
        );
      }
      _.each(userFilters, (userFilter) => filters.push(userFilter));
      if (this.props.academyId) {
        filters.push(`(academy_id:"${this.props.academyId}")`);
      }

      // Note that analyticsTags are not added here,
      // as we don't consider this a proper search and shouldn't be included in search analytics
      this.props.search('*', this.afterSearch.bind(this, searchId), {
        offset: 0,
        length: 250,
        filters: filters.join(' AND '),
      });
    } else {
      this.setState({ clearFilteringStateAfterUpdate: true });
      this.props.clear();
    }
  };

  afterSearch = (searchId, results) => {
    this.props.filter(results, (filteredResults) => {
      let formattedResults = this.props.format(filteredResults);
      formattedResults = _.flatten(formattedResults);

      this.props.afterSearch(searchId, formattedResults);
    });
  };

  clearFilteringState = () => {
    this.props.updateFilteringState(false);
    this.props.updateFilteredState(false);
    this.props.renderResults(null);
  };
}

const AcademyContentFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  .academy-content-filter-button {
    margin-right: 16px;
  }
  ${(props) =>
    props.align == 'right' &&
    `
    justify-content: flex-end;
    .academy-content-filter-button {
      margin-left: 16px;
      margin-right: 0;
    }
  `}
  ${(props) =>
    props.align == 'center' &&
    `
    justify-content: center;
    .academy-content-filter-button {
      margin-left: 8px;
      margin-right: 8px;
    }
  `}
  label {
    text-transform: capitalize;
  }
  .dropdown--list {
    border-radius: 6px;
    box-shadow: 0 4px 24px 2px rgba(20, 21, 22, 0.16);
    background-color: #fff;
    font-size: 13px;
    width: 250px;
    padding: 5px;
    li {
      padding: 8px 15px;
      border: none;
      label {
        display: flex;
        align-items: center;
        cursor: pointer;
        input {
          display: inline-block;
          margin-right: 15px;
        }
      }
    }
  }
`;

const AcademyContentFilterButton = styled.div`
  cursor: pointer;
  border: solid 1px #ccc;
  border-radius: 16px;
  font-size: 14px;
  color: #1b324b;
  padding: 5px 20px;
  transition: all 0.3s ease-in-out;
  ${(props) =>
    props.active &&
    `
    background-color: ${props.activeColor};
    color: ${props.activeTextColor};
  `}
`;

const AcademyContentFilters = withSearchResultsManagement(
  withAlgoliaSearch(AcademyContentFiltersBase, AcademyIndices.getAll()),
);
export default AcademyContentFilters;
